import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';

import { ChevronDownIcon, PlusIcon, SquareLibraryIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { LibraryCreateModal } from '~/components/LibraryBeta/LibraryCreateModal/LibraryCreateModal';
import { SubSideNavSectionHeader } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavSectionHeader';
import { WorkspaceSidebarLibrariesMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesMoreDropdown';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateLibrary } from '~/utils/permissions/libraryPermissions';

export const WorkspaceSidebarLibrariesHeader = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const [showLibraryCreateModal] = useAirModal(LibraryCreateModal);
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const canCreateLibraries = canCreateLibrary(workspacePermissions);

  const handleCreateLibrary = useCallback(() => {
    showLibraryCreateModal();
  }, [showLibraryCreateModal]);

  const actions = useMemo(
    () => (
      <div className="flex items-center gap-1">
        <WorkspaceSidebarLibrariesMoreDropdown />
        {canCreateLibraries && (
          <IconButton
            adsIcon
            appearance="ghost"
            color="gray"
            label="Create new library"
            onClick={handleCreateLibrary}
            size="small"
            icon={PlusIcon}
          />
        )}
      </div>
    ),
    [canCreateLibraries, handleCreateLibrary],
  );

  const header = useMemo(
    () =>
      ui2026 ? (
        <SubSideNavSectionHeader
          actions={actions}
          alwaysShowActions
          collapsed={collapsed}
          data-testid="LIBRARIES_SUB_SIDE_NAV_LIBRARIES_HEADER"
          icon={SquareLibraryIcon}
          label="Libraries"
          onToggle={toggleCollapsed}
        />
      ) : (
        <WorkspaceSidebarSectionHeader>
          <Button
            appearance="ghost"
            className="ads:text-metadata text-gray-9 ads:hover:bg-hover-subtle hover:bg-gray-5"
            color="gray"
            onClick={toggleCollapsed}
            size="small"
            suffix={
              <ChevronDownIcon className={classNames('transition-transform', { '-rotate-90': collapsed })} size="sm" />
            }
          >
            Libraries
          </Button>
          {actions}
        </WorkspaceSidebarSectionHeader>
      ),
    [actions, collapsed, toggleCollapsed, ui2026],
  );

  return header;
});

WorkspaceSidebarLibrariesHeader.displayName = 'WorkspaceSidebarLibrariesHeader';
